import { PageConfig } from "./pageConfig.types";
import {
  FormViewSettings,
  PortalUser,
  SaveViewRequest,
  StoredView,
  StoredViewSummary,
} from "./formViewStorage.types";
import {
  FormViewChangeKind,
  FormViewVersionSummary,
} from "../builder/VersionHistoryModal.types";

/**
 * Between the storage contract and the designer. The one place that knows the service
 * answers in Ticketing's casing and holds the layout as a JSON string.
 */

interface StoredViewResponse {
  Id?: string;
  Name?: string;
  ViewId?: string;
  ConfigJson?: string | null;
  ConditionsJson?: string | null;
  Sequence?: number;
  Grants?: string[] | null;
  /** The service says this the positive way round; the designer talks in switching off. */
  IsActive?: boolean;
}

export function adaptStoredViewSummary(row: StoredViewResponse) {
  return {
    id: row.Id ?? "",
    name: row.Name ?? "",
    viewId: row.ViewId ?? "",
    sequence: row.Sequence ?? 0,
    // Always a list: "nobody is named" means public, so it must not look like "not read".
    grants: Array.isArray(row.Grants) ? row.Grants : [],
    conditionsJson: row.ConditionsJson ?? null,
    // Only an explicit `false` switches a view off: a field that did not arrive means
    // a view that is on, which is what a row the service could not say anything about
    // should be treated as.
    deactivated: row.IsActive === false,
  } as StoredViewSummary;
}

/** Sorted again rather than trusting the service, as the order decides which view a record opens on. */
export function adaptStoredViewSummaries(rows: unknown) {
  return Array.isArray(rows)
    ? rows
        .map((row) => adaptStoredViewSummary(row as StoredViewResponse))
        .sort(
          (left, right) =>
            left.sequence - right.sequence || left.name.localeCompare(right.name)
        )
    : [];
}

/** A stored view with its layout parsed, or null when it cannot be read - the caller falls back to the shipped one. */
export function adaptStoredView(row: unknown): StoredView | null {
  if (!row || typeof row !== "object") {
    return null;
  }

  const summary = adaptStoredViewSummary(row as StoredViewResponse);
  const configJson = (row as StoredViewResponse).ConfigJson;

  if (!summary.id || !configJson) {
    return null;
  }

  try {
    return { ...summary, config: JSON.parse(configJson) as PageConfig };
  } catch {
    return null;
  }
}

interface StoredVersionResponse {
  Id?: string;
  VersionNumber?: number;
  Name?: string;
  ChangeKind?: string | null;
  ConfigJson?: string | null;
  CreatedOn?: string;
  CreatedByName?: string | null;
}

export function adaptVersionSummaries(rows: unknown): FormViewVersionSummary[] {
  return Array.isArray(rows)
    ? rows.map((row: StoredVersionResponse) => ({
        id: row.Id ?? "",
        versionNumber: row.VersionNumber ?? 0,
        name: row.Name ?? "",
        // Older rows predate the column, and every one of those was a save.
        changeKind: (row.ChangeKind || "Save") as FormViewChangeKind,
        createdOn: row.CreatedOn ?? "",
        createdByName: row.CreatedByName ?? "",
      }))
    : [];
}

/** A version's layout, or null when it cannot be read - the canvas keeps what it has. */
export function adaptVersionConfig(row: unknown): PageConfig | null {
  const configJson = (row as StoredVersionResponse | null)?.ConfigJson;

  if (!configJson) {
    return null;
  }

  try {
    return JSON.parse(configJson) as PageConfig;
  } catch {
    return null;
  }
}

/** The payload the save endpoint takes: the name beside the layout, not inside it. */
export function toSaveRequest(request: SaveViewRequest) {
  return {
    Id: request.id,
    EntityName: request.entityName,
    ViewId: request.viewId,
    Name: request.name,
    ConfigJson: JSON.stringify(request.config),
    // Undefined leaves the audience alone; the service reads [] as "make this public".
    Grants: request.grants,
  };
}

interface PortalUserResponse {
  SystemUserId?: string;
  FullName?: string | null;
  EmailAddress?: string | null;
}

/** The people a view can be granted to, named by their lowercased email address. */
export function adaptPortalUsers(rows: unknown): PortalUser[] {
  return Array.isArray(rows)
    ? rows
        .map((row: PortalUserResponse) => {
          const emailAddress = (row.EmailAddress ?? "").trim().toLowerCase();

          return {
            id: row.SystemUserId ?? "",
            // Trimmed before the fallback, or an address of spaces beats a good name.
            name: emailAddress || (row.FullName ?? "").trim(),
            emailAddress,
          };
        })
        .filter((user) => user.id && user.name)
    : [];
}

/** No row means the organisation has changed nothing, so the shipped behaviour applies. */
export function adaptFormViewSettings(row: unknown): FormViewSettings {
  return {
    defaultDeactivated:
      (row as { DefaultDeactivated?: boolean } | null)?.DefaultDeactivated ===
      true,
  };
}
